import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import type { EmployeeDocument } from '../types';
import {
  fetchEmployees, fetchEmployeeDocuments,
  createEmployeeDocumentApi, acknowledgeDocumentApi,
} from '../services/api';
import { qk } from '../services/queryKeys';
import Chip from '../components/Chip';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import Empty from '../components/Empty';
import Alert from '../components/Alert';
import Avatar from '../components/Avatar';
import DatePicker from '../components/DatePicker';

const RETENTION_LABELS: Record<string, string> = {
  statutory_locked:       'Statutory (locked)',
  business:               'Business',
  non_statutory_erasable: 'Non-statutory (erasable)',
};

function DocRow({
  doc, today, onAck,
}: {
  doc: EmployeeDocument;
  today: string;
  onAck?: (doc: EmployeeDocument) => void;
}) {
  const isExpired = doc.status === 'expired' || (doc.expiresAt && doc.expiresAt < today);
  const expiresInDays = doc.expiresAt
    ? Math.ceil((new Date(doc.expiresAt).getTime() - Date.now()) / 86400000)
    : null;

  return (
    <div className="sk-listrow">
      <div style={{ width: 40, height: 40, borderRadius: 10, background: isExpired ? 'var(--danger-soft)' : 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="doc" size={18} color={isExpired ? 'var(--danger)' : 'var(--accent)'} />
      </div>
      <div className="row-main">
        <div className="row-title">{doc.title}</div>
        <div className="row-sub">{doc.category} · v{doc.version}</div>
        {doc.retentionYears && (
          <div className="row-meta">Retained for {doc.retentionYears} year{doc.retentionYears > 1 ? 's' : ''}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {isExpired && <Chip variant="danger">Expired</Chip>}
        {!isExpired && expiresInDays !== null && expiresInDays <= 30 && (
          <Chip variant="warn">Expires in {expiresInDays}d</Chip>
        )}
        {!isExpired && expiresInDays !== null && expiresInDays > 30 && (
          <Chip variant="neutral">Expires {doc.expiresAt}</Chip>
        )}
        {doc.requiresAck && doc.ackedAt && <Chip variant="success">Acknowledged</Chip>}
        {doc.requiresAck && !doc.ackedAt && onAck && (
          <Button variant="accent" size="sm" onClick={() => onAck(doc)}>Acknowledge</Button>
        )}
        {doc.requiresAck && !doc.ackedAt && !onAck && (
          <Chip variant="warn">Pending ack</Chip>
        )}
        <Button variant="ghost" size="sm" disabled>
          <Icon name="download" size={13} color="var(--ink-3)" /> E-sign
        </Button>
      </div>
    </div>
  );
}

// ─── HR view ─────────────────────────────────────────────────────────────────
function HrDocuments() {
  const { toast } = useAppStore();
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    title: '',
    storageRef: '',
    version: '1',
    expiresOn: '',
    retention: 'business' as 'statutory_locked' | 'business' | 'non_statutory_erasable',
  });

  const { data: employees = [] } = useQuery({ queryKey: qk.employees(), queryFn: fetchEmployees });

  const { data: docs = [], isLoading: docsLoading } = useQuery({
    queryKey: qk.documents(selectedEmpId),
    queryFn: () => fetchEmployeeDocuments(selectedEmpId),
    enabled: !!selectedEmpId,
  });

  const addMutation = useMutation({
    mutationFn: createEmployeeDocumentApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.documents(selectedEmpId) });
      toast('Document added');
      setShowAdd(false);
      setForm({ title: '', storageRef: '', version: '1', expiresOn: '', retention: 'business' });
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const filteredEmps = employees.filter(e => {
    const q = search.toLowerCase();
    return `${e.firstName} ${e.lastName ?? ''} ${e.employeeCode ?? ''}`.toLowerCase().includes(q);
  });

  const selectedEmp = employees.find(e => e.id === selectedEmpId);

  return (
    <div>
      <div className="sk-page-header">
        <div>
          <h1>Documents</h1>
          <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', marginTop: 2 }}>Issue and manage employee documents</p>
        </div>
        <Button
          variant="accent"
          disabled={!selectedEmpId}
          onClick={() => { setForm({ title: '', storageRef: '', version: '1', expiresOn: '', retention: 'business' }); setShowAdd(true); }}
        >
          <Icon name="plus" size={14} color="white" /> Add document
        </Button>
      </div>

      {/* Employee selector */}
      <div className="t-card" style={{ marginBottom: 20 }}>
        <div className="card-head" style={{ paddingBottom: 12 }}>
          <h3>Select employee</h3>
          {selectedEmp && (
            <button onClick={() => setSelectedEmpId('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', padding: 0 }}>
              Clear
            </button>
          )}
        </div>
        <div className="card-body" style={{ paddingTop: 8 }}>
          {selectedEmp ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '2px solid var(--accent)', borderRadius: 10, background: 'var(--accent-soft)' }}>
              <Avatar firstName={selectedEmp.firstName} lastName={selectedEmp.lastName} color={selectedEmp.avatarColor} size="sm" />
              <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--ink)' }}>{selectedEmp.firstName} {selectedEmp.lastName}</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{selectedEmp.jobTitle} · {selectedEmp.employeeCode}</div>
              </div>
            </div>
          ) : (
            <div>
              <input
                className="t-input"
                placeholder="Search by name or employee code…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ marginBottom: 10 }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 260, overflowY: 'auto' }}>
                {filteredEmps.slice(0, 20).map(emp => (
                  <div
                    key={emp.id}
                    onClick={() => { setSelectedEmpId(emp.id); setSearch(''); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--neutral-soft)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Avatar firstName={emp.firstName} lastName={emp.lastName} color={emp.avatarColor} size="sm" />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--ink)' }}>{emp.firstName} {emp.lastName}</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{emp.jobTitle} · {emp.employeeCode}</div>
                    </div>
                  </div>
                ))}
                {filteredEmps.length === 0 && (
                  <div style={{ padding: '12px 4px', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No employees match "{search}"</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documents list */}
      {selectedEmpId && (
        <div className="t-card">
          <div className="card-head" style={{ paddingBottom: 12 }}>
            <h3>Documents — {selectedEmp?.firstName} {selectedEmp?.lastName}</h3>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{docs.length} document{docs.length !== 1 ? 's' : ''}</span>
          </div>
          {docsLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="t-spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : docs.length === 0 ? (
            <Empty title="No documents" description="Add the first document for this employee" icon="doc" />
          ) : (
            <div>
              {docs.map(d => (
                <DocRow key={d.id} doc={d} today={today} />
              ))}
            </div>
          )}
        </div>
      )}

      {!selectedEmpId && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>
          Select an employee above to view their documents
        </div>
      )}

      {/* Add document modal */}
      {showAdd && selectedEmp && (
        <Modal
          title="Add document"
          onClose={() => setShowAdd(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button
                variant="accent"
                disabled={!form.title.trim() || addMutation.isPending}
                onClick={() => addMutation.mutate({
                  employeeId: selectedEmpId,
                  title: form.title.trim(),
                  storageRef: form.storageRef.trim() || undefined,
                  version: form.version ? parseInt(form.version, 10) : 1,
                  expiresOn: form.expiresOn || undefined,
                  retention: form.retention,
                })}
              >
                {addMutation.isPending ? 'Saving…' : 'Add document'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--neutral-soft)', borderRadius: 8, marginBottom: 16 }}>
            <Avatar firstName={selectedEmp.firstName} lastName={selectedEmp.lastName} color={selectedEmp.avatarColor} size="sm" />
            <div>
              <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{selectedEmp.firstName} {selectedEmp.lastName}</div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{selectedEmp.jobTitle}</div>
            </div>
          </div>

          <div className="form-row">
            <label>Document title</label>
            <input className="t-input" placeholder="e.g. Employment contract, Offer letter" autoFocus value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Document URL <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(optional)</span></label>
            <input className="t-input" placeholder="Paste a link — Google Drive, S3, SharePoint…" value={form.storageRef} onChange={e => setForm(f => ({ ...f, storageRef: e.target.value }))} />
          </div>
          <div className="form-grid">
            <div className="form-row">
              <label>Version</label>
              <input className="t-input" type="number" min={1} value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} />
            </div>
            <div className="form-row">
              <label>Expires on <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(optional)</span></label>
              <DatePicker value={form.expiresOn} onChange={v => setForm(f => ({ ...f, expiresOn: v }))} placeholder="No expiry" />
            </div>
          </div>
          <div className="form-row">
            <label>Retention class</label>
            <select className="t-input" value={form.retention} onChange={e => setForm(f => ({ ...f, retention: e.target.value as typeof f.retention }))}>
              <option value="business">Business</option>
              <option value="statutory_locked">Statutory (locked — cannot be erased)</option>
              <option value="non_statutory_erasable">Non-statutory (erasable)</option>
            </select>
          </div>
          <Alert variant="info">
            No file upload — paste the document URL above. Actual storage and e-sign will route through SK Sign when available.
          </Alert>
        </Modal>
      )}
    </div>
  );
}

// ─── ESS / MSS view ───────────────────────────────────────────────────────────
function EssDocuments() {
  const { profileId, employeeId, modal, openModal, closeModal, toast } = useAppStore();
  const myId = employeeId || profileId;
  const today = new Date().toISOString().slice(0, 10);

  const [acking, setAcking] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);

  const { data: docs = [], isLoading: loading, refetch } = useQuery({
    queryKey: qk.documents(myId),
    queryFn: () => fetchEmployeeDocuments(myId),
    enabled: !!employeeId,
  });

  const pendingDocId = modal.type === 'acknowledge_doc' ? (modal.props?.docId as string) : null;
  const pendingDoc = pendingDocId ? docs.find(d => d.id === pendingDocId) : null;

  async function handleAcknowledge() {
    if (!pendingDocId || !pendingDoc) return;
    setAcking(true);
    try {
      const ver = typeof pendingDoc.version === 'number'
        ? pendingDoc.version
        : parseInt(String(pendingDoc.version), 10) || 1;
      await acknowledgeDocumentApi(pendingDocId, pendingDoc.employeeId, ver);
      refetch();
      toast('Document acknowledged');
      setAckChecked(false);
      closeModal();
    } catch {
      toast('Failed to acknowledge document', 'danger');
    } finally {
      setAcking(false);
    }
  }

  return (
    <div>
      <div className="sk-page-header">
        <div>
          <h1>Documents</h1>
          <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', marginTop: 2 }}>
            Acknowledge new versions · e-sign via SK Sign coming soon
          </p>
        </div>
      </div>

      <div className="t-card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="t-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : docs.length === 0 ? (
          <Empty title="No documents" description="Documents shared by HR will appear here" icon="doc" />
        ) : (
          <div>
            {docs.map(d => (
              <DocRow
                key={d.id}
                doc={d}
                today={today}
                onAck={doc => { setAckChecked(false); openModal('acknowledge_doc', { docId: doc.id }); }}
              />
            ))}
          </div>
        )}
      </div>

      {modal.type === 'acknowledge_doc' && pendingDoc && (
        <Modal
          title="Acknowledge document"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={closeModal} disabled={acking}>Cancel</Button>
              <Button variant="accent" onClick={handleAcknowledge} disabled={acking || !ackChecked}>
                {acking
                  ? <><div className="t-spinner" style={{ width: 14, height: 14, borderTopColor: 'white' }} /> Confirming…</>
                  : <><Icon name="check" size={14} color="white" /> Confirm acknowledgement</>
                }
              </Button>
            </>
          }
        >
          <div style={{ padding: '12px 16px', background: 'var(--paper-2)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 16 }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{pendingDoc.title}</p>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>{pendingDoc.category} · version {pendingDoc.version}</p>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
            <input type="checkbox" checked={ackChecked} onChange={e => setAckChecked(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', lineHeight: 1.5 }}>
              I have read and accept version {pendingDoc.version} of this document
            </span>
          </label>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
            This acknowledgement will be timestamped and recorded.
          </p>
        </Modal>
      )}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function Documents() {
  const { role } = useAppStore();
  return role === 'hr' ? <HrDocuments /> : <EssDocuments />;
}
